import { CalendarIcon, FunnelIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

interface DateRangeFilterProps {
  dateRange: DateRange;
  onFilterChange: (range: DateRange) => void;
  className?: string;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateRange,
  onFilterChange,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localRange, setLocalRange] = useState<DateRange>(dateRange);
  const [validationError, setValidationError] = useState<string | null>(null);

  const todayStr = new Date().toISOString().split('T')[0];

  // Sync local range when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      setLocalRange(dateRange);
      setValidationError(null);
    }
  }, [isOpen, dateRange]);

  // Determine active preset based on dateRange prop
  const getActivePreset = () => {
    if (!dateRange.startDate && !dateRange.endDate) return 'all';

    // Check presets
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start30 = new Date(today);
    start30.setDate(today.getDate() - 30);
    if (
      dateRange.startDate === start30.toISOString().split('T')[0] &&
      dateRange.endDate === today.toISOString().split('T')[0]
    )
      return '30d';

    const start90 = new Date(today);
    start90.setDate(today.getDate() - 90);
    if (
      dateRange.startDate === start90.toISOString().split('T')[0] &&
      dateRange.endDate === today.toISOString().split('T')[0]
    )
      return '90d';

    const startYtd = new Date(today.getFullYear(), 0, 1);
    if (
      dateRange.startDate === startYtd.toISOString().split('T')[0] &&
      dateRange.endDate === today.toISOString().split('T')[0]
    )
      return 'ytd';

    return 'custom';
  };

  const activePreset = getActivePreset();

  const presets = [
    { label: 'Last 30 Days', id: '30d' },
    { label: 'Last 90 Days', id: '90d' },
    { label: 'Year to Date', id: 'ytd' },
    { label: 'All Time', id: 'all' },
  ];

  const formatDateLabel = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
    });
  };

  const applyPreset = (presetId: string) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (presetId === '30d') {
      start.setDate(today.getDate() - 30);
    } else if (presetId === '90d') {
      start.setDate(today.getDate() - 90);
    } else if (presetId === 'ytd') {
      start = new Date(today.getFullYear(), 0, 1);
    } else if (presetId === 'all') {
      onFilterChange({ startDate: null, endDate: null });
      setIsOpen(false);
      return;
    }

    onFilterChange({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    });
    setIsOpen(false);
  };

  const handleCustomDateChange = (type: 'start' | 'end', value: string) => {
    setValidationError(null);
    setLocalRange((prev) => ({
      ...prev,
      [type === 'start' ? 'startDate' : 'endDate']: value || null,
    }));
  };

  const handleApplyCustom = () => {
    // Basic validation
    if (localRange.startDate && localRange.endDate) {
      if (localRange.startDate > localRange.endDate) {
        setValidationError('Start date cannot be after end date');
        return;
      }
      if (localRange.startDate > todayStr || localRange.endDate > todayStr) {
        setValidationError('Future dates are not allowed');
        return;
      }
    }

    onFilterChange(localRange);
    setIsOpen(false);
  };

  // Initialize with default 90 days on mount if needed
  // React.useEffect(() => { applyPreset('90d'); }, []);

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 whitespace-nowrap"
      >
        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
        {activePreset === 'custom'
          ? `${formatDateLabel(dateRange.startDate)} - ${formatDateLabel(dateRange.endDate)}`
          : presets.find((p) => p.id === activePreset)?.label || 'Select Date Range'}
        <FunnelIcon className="h-4 w-4 ml-2 text-gray-400" />
      </button>

      {isOpen && (
        <div className="origin-top-left absolute left-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1" role="menu" aria-orientation="vertical">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Quick Select
            </div>
            <div className="grid grid-cols-2 gap-2 px-4 pb-2">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(preset.id)}
                  className={`text-sm px-2 py-1 rounded-md text-center ${
                    activePreset === preset.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="border-t border-gray-100 my-1"></div>

            <div className="px-4 py-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Custom Date Range
              </div>

              {validationError && (
                <div className="mb-2 p-1.5 bg-red-50 border border-red-100 rounded text-[10px] text-red-600">
                  {validationError}
                </div>
              )}

              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-500">Starts</label>
                  <input
                    type="date"
                    value={localRange.startDate || ''}
                    max={todayStr}
                    onChange={(e) => handleCustomDateChange('start', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Ends</label>
                  <input
                    type="date"
                    value={localRange.endDate || ''}
                    max={todayStr}
                    onChange={(e) => handleCustomDateChange('end', e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 mt-2 px-4 py-3 bg-gray-50 flex items-center justify-between rounded-b-md">
              <button
                onClick={() => setIsOpen(false)}
                className="text-xs font-medium text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyCustom}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-semibold rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Apply Range
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
